import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileDown, Send, Loader2 } from "lucide-react";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Salary() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payouts, setPayouts] = useState({}); // {employee_id: payout doc}
  const [paying, setPaying] = useState({}); // {employee_id: bool}
  const [payingAll, setPayingAll] = useState(false);
  const [payCfg, setPayCfg] = useState(null);

  const loadPayouts = useCallback(async () => {
    try {
      const { data } = await api.get(`/payouts/${month}`);
      const map = {};
      data.forEach((p) => { map[p.employee_id] = p; });
      setPayouts(map);
    } catch {}
  }, [month]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/payouts/config`); setPayCfg(data); } catch {}
    })();
  }, []);
  useEffect(() => { loadPayouts(); }, [loadPayouts]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/salary/${month}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const download = async () => {
    try {
      const res = await fetch(`${API}/salary/${month}/export`, { credentials: "include" });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Salary_${month}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const downloadPayslip = async (empId, name) => {
    try {
      const res = await fetch(`${API}/salary/${month}/payslip/${empId}`, { credentials: "include" });
      if (!res.ok) throw new Error("payslip failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (name || "employee").replace(/[^a-z0-9_-]+/gi, "_");
      a.href = url; a.download = `Payslip_${safe}_${month}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Payslip downloaded");
    } catch (e) {
      toast.error("Payslip download failed");
    }
  };

  const payOne = async (empId) => {
    setPaying((s) => ({ ...s, [empId]: true }));
    try {
      const { data: doc } = await api.post(`/payouts/${month}/${empId}`, { mode: "IMPS" });
      setPayouts((p) => ({ ...p, [empId]: doc }));
      toast.success(`Payout queued: ${doc.razorpay_status || "processing"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPaying((s) => ({ ...s, [empId]: false }));
    }
  };

  const payAll = async () => {
    if (!window.confirm(`Trigger salary payouts for all employees for ${month}?`)) return;
    setPayingAll(true);
    try {
      const { data: res } = await api.post(`/payouts/${month}/bulk`, { mode: "IMPS" });
      toast.success(`Processed ${res.count} employees. Check the Status column.`);
      loadPayouts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPayingAll(false);
    }
  };

  const statusBadge = (empId) => {
    const p = payouts[empId];
    if (!p) return <span className="text-[11px] text-slate-400">—</span>;
    const s = (p.razorpay_status || "").toLowerCase();
    const map = {
      processed: "bg-emerald-100 text-emerald-800 border-emerald-200",
      processing: "bg-sky-100 text-sky-800 border-sky-200",
      queued: "bg-amber-100 text-amber-800 border-amber-200",
      pending: "bg-amber-100 text-amber-800 border-amber-200",
      reversed: "bg-rose-100 text-rose-800 border-rose-200",
      failed: "bg-rose-100 text-rose-800 border-rose-200",
      cancelled: "bg-slate-100 text-slate-700 border-slate-200",
      rejected: "bg-rose-100 text-rose-800 border-rose-200",
    };
    const cl = map[s] || "bg-slate-100 text-slate-700 border-slate-200";
    return <span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cl}`}>{s || "—"}</span>;
  };

  const totals = data?.totals || {};

  return (
    <div className="p-8 space-y-5" data-testid="salary-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold">Monthly Salary</h1>
          <p className="text-sm text-slate-500 mt-1">Auto-computed from attendance, OT, incentives and PF/ESI settings.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs text-slate-500 font-mono uppercase">Month</Label>
            <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="w-40" data-testid="salary-month" />
          </div>
          <Button onClick={download} data-testid="salary-download-btn"><Download size={16} className="mr-1"/> Export Excel</Button>
          <Button
            onClick={payAll}
            disabled={payingAll || !payCfg?.account_number_present}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="pay-all-btn"
            title={!payCfg?.account_number_present ? "Add RAZORPAYX_ACCOUNT_NUMBER in backend/.env to enable payouts" : "Pay all employees"}
          >
            {payingAll ? <Loader2 size={16} className="mr-1 animate-spin"/> : <Send size={16} className="mr-1"/>}
            Pay All
          </Button>
        </div>
      </div>

      {payCfg && !payCfg.account_number_present && (
        <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm" data-testid="payout-warning">
          <b>Razorpay Payouts inactive.</b> Add <code className="font-mono">RAZORPAYX_ACCOUNT_NUMBER</code> (found in RazorpayX Dashboard → Account Settings) to <code>backend/.env</code> and restart the backend to enable one-click salary payouts.
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="att-table min-w-full">
              <thead>
                <tr>
                  {["Employee","Salary Days","Paid Leave","Working Days","Fixed Salary","Basic","PF","OT hrs","Total Incentive","Total Earn","ESI","Take Home","Employer PF","Employer ESI","Payslip","Payout"].map((h,i)=>(
                    <th key={h} className={i===0?"text-left sticky-col":"text-right"} style={i===0?{minWidth:200}:{}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.rows||[]).map((r)=>(
                  <tr key={r.employee_id} data-testid={`sal-row-${r.code}`}>
                    <td className="sticky-col">
                      <div className="font-body font-medium text-slate-900">{r.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{r.code}</div>
                    </td>
                    <Num v={r.present_days} />
                    <Num v={r.paid_leave} />
                    <Num v={r.salary_days} />
                    <Num v={r.fixed_salary} />
                    <Num v={r.basic} />
                    <Num v={r.pf} />
                    <Num v={r.ot_hours} />
                    <Num v={r.total_incentive} />
                    <Num v={r.total_earn} bold />
                    <Num v={r.esi} />
                    <Num v={r.take_home} bold color="text-emerald-700" />
                    <Num v={r.employer_pf} />
                    <Num v={r.employer_esi} />
                    <td className="text-center">
                      <button
                        onClick={()=>downloadPayslip(r.employee_id, r.name)}
                        data-testid={`payslip-${r.code}`}
                        className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700 inline-flex items-center gap-1 text-xs font-medium"
                        title="Download PDF payslip"
                      >
                        <FileDown size={14}/> PDF
                      </button>
                    </td>
                    <td className="text-center">
                      <div className="flex flex-col items-center gap-1">
                        {statusBadge(r.employee_id)}
                        <button
                          onClick={()=>payOne(r.employee_id)}
                          disabled={paying[r.employee_id] || r.take_home<=0}
                          data-testid={`pay-${r.code}`}
                          className="text-[11px] font-medium text-emerald-700 hover:underline disabled:opacity-40 disabled:no-underline inline-flex items-center gap-1"
                          title={r.take_home<=0 ? "Take-home is zero" : "Send salary via RazorpayX"}
                        >
                          {paying[r.employee_id]
                            ? <><Loader2 size={12} className="animate-spin"/> Sending…</>
                            : (payouts[r.employee_id] ? "Retry" : "Pay")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {data?.rows?.length===0 && (
                  <tr><td colSpan={16} className="text-center py-10 text-slate-400">No employees found for this month.</td></tr>
                )}
              </tbody>
              {data?.rows?.length>0 && (
                <tfoot>
                  <tr className="bg-slate-900 text-slate-100 font-bold">
                    <td className="sticky-col bg-slate-900 text-slate-100">TOTALS</td>
                    <td/><td/><td/>
                    <Num v={totals.fixed_salary} />
                    <Num v={totals.basic} />
                    <Num v={totals.pf} />
                    <td/>
                    <Num v={totals.total_incentive} />
                    <Num v={totals.total_earn} />
                    <Num v={totals.esi} />
                    <Num v={totals.take_home} color="text-emerald-300" />
                    <Num v={totals.employer_pf} />
                    <Num v={totals.employer_esi} />
                    <td/>
                    <td/>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Num({ v, bold, color }) {
  const val = Number(v || 0);
  return (
    <td className={`text-right num ${bold?"font-semibold":""} ${color||""}`}>
      {val.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
    </td>
  );
}
